import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../store/hooks";
import { API_BASE_URL } from "../config/env";

// the agent's display name in chat toasts (matches the backend CHAT_SENDER_LABEL).
const SENDER_LABEL = "sitebot";
const PREVIEW_MAX = 120;
// collapse a rapid multi-bubble burst in one chat into a single toast.
const COOLDOWN_MS = 5_000;

interface ChatStreamEvent {
  type: string;
  chatType?: "general" | "personal";
  // "general" | "aotd" — "aotd" tags an ephemeral waiting-room message that lives only in the
  // AOTD panel and must never surface as a general toast (an ephemeral one has no history home).
  room?: "general" | "aotd";
  sender?: string;
  senderType?: "user" | "agent";
  text?: string;
  ts?: string;
}

function preview(text: string): string {
  const t = (text || "").trim();
  return t.length <= PREVIEW_MAX ? t : t.slice(0, PREVIEW_MAX - 1) + "…";
}

/**
 * Headless, app-level listener that pops an in-app toast when a NEW sitebot chat message
 * arrives (general or personal) while the user is on the site but not viewing the chat.
 *
 * Subscribes to the backend SSE stream (`/agent/chat/stream`) for instant delivery — general
 * events fan out to everyone; personal events are targeted to this user. Push notifications
 * handle the "away" case, and the service worker suppresses the chat push while a tab is
 * visible, so push and toast never double-fire.
 */
const ChatToastListener: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const username = useAppSelector((s) => s.auth.username);
  const token = useAppSelector((s) => s.auth.token);
  const loggedIn = useAppSelector(
    (s) => s.auth.bootstrapStatus === "ready" && !!s.auth.token,
  );

  // on the full chat page the user sees messages live — don't toast there.
  const onChatPage = location.pathname.startsWith("/agent-chat");

  // refs so the long-lived SSE handler reads current values without reconnecting on every
  // navigation / auth tick.
  const onChatPageRef = useRef(onChatPage);
  const usernameRef = useRef(username);
  const lastToastAt = useRef<{ general: number; personal: number }>({ general: 0, personal: 0 });
  useEffect(() => {
    onChatPageRef.current = onChatPage;
  }, [onChatPage]);
  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  const openChat = useCallback(
    (tab: "group" | "personal") => navigate(`/agent-chat?tab=${tab}`),
    [navigate],
  );

  const showToast = useCallback(
    (title: string, body: string, tab: "group" | "personal") => {
      toast.info(
        <div>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{title}</div>
          <div style={{ fontSize: "0.9em", opacity: 0.9 }}>{body}</div>
        </div>,
        { autoClose: 5000, onClick: () => openChat(tab) },
      );
    },
    [openChat],
  );

  useEffect(() => {
    if (!loggedIn || !token) return;
    const url = `${API_BASE_URL}/agent/chat/stream?access_token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.onmessage = (e) => {
      let ev: ChatStreamEvent;
      try {
        ev = JSON.parse(e.data) as ChatStreamEvent;
      } catch {
        return;
      }
      if (!ev || ev.type !== "message" || !ev.chatType) return;
      // AOTD waiting-room traffic belongs to the AOTD panel only. Ephemeral aotd messages have
      // no history and must never toast as if they were general chat.
      if (ev.room === "aotd") return;
      if (onChatPageRef.current) return; // viewing the chat — no toast

      // skip our own general messages (personal events are always the agent's incoming reply).
      if (
        ev.chatType === "general" &&
        ev.senderType === "user" &&
        ev.sender === usernameRef.current
      ) {
        return;
      }

      // collapse rapid bursts per chat into one toast.
      const now = Date.now();
      if (now - lastToastAt.current[ev.chatType] < COOLDOWN_MS) return;
      lastToastAt.current[ev.chatType] = now;

      if (ev.chatType === "general") {
        const who = ev.senderType === "agent" ? SENDER_LABEL : ev.sender || "someone";
        showToast(`💬 (general) ${who}`, preview(ev.text || ""), "group");
      } else {
        showToast(`💬 ${SENDER_LABEL}`, preview(ev.text || ""), "personal");
      }
    };

    // EventSource auto-reconnects on transient drops; the effect re-opens with a fresh token
    // when it refreshes (token is in the dep list).
    es.onerror = () => {
      /* no-op — rely on built-in reconnect */
    };

    return () => es.close();
  }, [loggedIn, token, showToast]);

  return null;
};

export default ChatToastListener;
